import { BarChart3, Calendar, TrendingUp, AlertCircle } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";

export default function LeaveBalance({ balance, detailed = false }) {
  const balanceEntries = Object.entries(balance || {});
  console.log(balanceEntries);

  const getBalanceStatus = (used, total) => {
    console.log("----------------");
    console.log(used, total);
    if (!total) return null; // No limit

    const percentage = (used / total) * 100;
    if (percentage >= 90)
      return {
        color: "text-red-600",
        label: "Critique",
        variant: "destructive",
      };
    if (percentage >= 70)
      return {
        color: "text-orange-600",
        label: "Attention",
        variant: "secondary",
      };
    return { color: "text-green-600", label: "Disponible", variant: "default" };
  };

  const formatBalanceKey = (key) => {
    console.log(key);

    const mapping = {
      congé_annuel: "Congé Annuel",
      congé_exceptionnel: "Congé Exceptionnel",
      congé_maladie: "Congé Maladie",
    };
    return mapping[key] || key;
  };

  if (!detailed) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <BarChart3 className="h-5 w-5" />
            Mes soldes de congés
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {balanceEntries.map(([key, data]) => {
            const status = getBalanceStatus(data.utilise, data.total);

            return (
              <div key={key} className="space-y-2">
                <div className="flex justify-between items-center">
                  <span className="text-sm font-medium">
                    {formatBalanceKey(key)}
                  </span>
                  {status && (
                    <Badge variant={status.variant} className="text-xs">
                      {status.label}
                    </Badge>
                  )}
                </div>

                {data.total ? (
                  <>
                    <Progress
                      value={(data.utilise / data.total) * 100}
                      className="h-2"
                    />
                    <div className="flex justify-between text-xs text-muted-foreground">
                      <span>{data.utilise} utilisé(s)</span>
                      <span>
                        {data.restant} restant(s) / {data.total}
                      </span>
                    </div>
                  </>
                ) : (
                  <div className="flex justify-between text-xs text-muted-foreground">
                    <span>{data.utilise} utilisé(s)</span>
                    <span>Illimité</span>
                  </div>
                )}
              </div>
            );
          })}
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calendar className="h-5 w-5" />
            Détail des soldes de congés
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 gap-6">
            {balanceEntries.map(([key, data]) => {
              const status = getBalanceStatus(data.utilise, data.total);
              key = key.toLowerCase().replace(/\s+/g, "_") || "unknown";

              return (
                <Card key={key} className="p-4">
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <h3 className="font-semibold text-lg">
                        {formatBalanceKey(key)}
                      </h3>
                      {status && (
                        <Badge variant={status.variant} className="mt-1">
                          {status.label}
                        </Badge>
                      )}
                    </div>

                    <div className="text-right">
                      {data.total ? (
                        <>
                          <div className="text-2xl font-bold">
                            {data.restant}
                            <span className="text-sm font-normal text-muted-foreground">
                              /{data.total}
                            </span>
                          </div>
                          <div className="text-sm text-muted-foreground">
                            jours restants
                          </div>
                        </>
                      ) : (
                        <>
                          <div className="text-2xl font-bold">
                            {data.utilise}
                          </div>
                          <div className="text-sm text-muted-foreground">
                            jours utilisés
                          </div>
                        </>
                      )}
                    </div>
                  </div>

                  {data.total && (
                    <div className="space-y-2">
                      <Progress
                        value={(data.utilise / data.total) * 100}
                        className="h-3"
                      />
                      <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground">
                          {data.utilise} jour(s) utilisé(s)
                        </span>
                        <span className={status?.color || ""}>
                          {((data.utilise / data.total) * 100).toFixed(1)}%
                          utilisé
                        </span>
                      </div>
                    </div>
                  )}

                  {/* Warning for high usage */}
                  {status?.label === "Critique" && (
                    <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2">
                      <AlertCircle className="h-4 w-4 text-red-600" />
                      <span className="text-sm text-red-800">
                        Attention: Vous avez utilisé plus de 90% de vos congés
                        annuels
                      </span>
                    </div>
                  )}

                  {status?.label === "Attention" && (
                    <div className="mt-4 p-3 bg-orange-50 border border-orange-200 rounded-lg flex items-center gap-2">
                      <AlertCircle className="h-4 w-4 text-orange-600" />
                      <span className="text-sm text-orange-800">
                        Il vous reste peu de jours de congés disponibles
                      </span>
                    </div>
                  )}
                </Card>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Annual Summary */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TrendingUp className="h-5 w-5" />
            Résumé annuel 2025
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="text-center p-4 bg-blue-50 rounded-lg">
              <div className="text-2xl font-bold text-blue-700">
                {Object.entries(balance)
                  .filter(([key]) => key !== "conge_maladie")
                  .reduce((sum, [, item]) => sum + item.utilise, 0)}
              </div>
              <div className="text-sm text-blue-600">Total jours pris</div>
            </div>

            <div className="text-center p-4 bg-green-50 rounded-lg">
              <div className="text-2xl font-bold text-green-700">
                {Object.values(balance)
                  .filter((item) => item.total)
                  .reduce((sum, item) => sum + item.restant, 0)}
              </div>
              <div className="text-sm text-green-600">Jours restants</div>
            </div>

            <div className="text-center p-4 bg-purple-50 rounded-lg">
              <div className="text-2xl font-bold text-purple-700">
                {Object.values(balance)
                  .filter((item) => item.total)
                  .reduce((sum, item) => sum + item.total, 0)}
              </div>
              <div className="text-sm text-purple-600">Total autorisé</div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
